import { baseApi as api } from "../../../contexts/endpoints/base_api";
import {
  NullSafePhoneNumberPayload,
  PhoneNumberPayload,
} from "../../../contexts/endpoints/shared/types";

const injectedApi = api.injectEndpoints({
  endpoints: (build) => ({
    getAccountOrganizations: build.query<
      GetAccountOrganizationsApiResponse,
      GetAccountOrganizationsApiArg
    >({
      query: () => ({ url: `/api/account/organizations` }),
      providesTags: ["Organizations"],
    }),
    getAccountMembershipStructure: build.query<
      GetAccountMembershipStructureApiResponse,
      GetAccountMembershipStructureApiArg
    >({
      query: (queryArg) => ({
        url: `/api/account/organizations/membershipStructure`,
        params: { orgId: queryArg.orgId },
      }),
      providesTags: ["Organizations"],
    }),
    postOrganizations: build.mutation<
      PostOrganizationsApiResponse,
      PostOrganizationsApiArg
    >({
      query: (queryArg) => ({
        url: `/api/account/organizations`,
        method: "POST",
        body: queryArg.newOrganizationPayload,
      }),
      invalidatesTags: ["Organizations"],
    }),
    putRequestOrgMembership: build.mutation<
      PutRequestOrgMembershipApiResponse,
      PutRequestOrgMembershipApiArg
    >({
      query: (queryArg) => ({
        url: `/api/account/organizations/requestMembership`,
        method: "PUT",
        body: queryArg.orgIdPayload,
      }),
      invalidatesTags: ["Organizations"],
    }),
    putAcceptOrgInvite: build.mutation<
      PutAcceptOrgInviteApiResponse,
      PutAcceptOrgInviteApiArg
    >({
      query: (queryArg) => ({
        url: `/api/account/organizations/acceptInvite`,
        method: "PUT",
        body: queryArg.orgIdPayload,
      }),
      invalidatesTags: ["Organizations"],
    }),
    putRejectOrgInvite: build.mutation<
      PutRejectOrgInviteApiResponse,
      PutRejectOrgInviteApiArg
    >({
      query: (queryArg) => ({
        url: `/api/account/organizations/rejectInvite`,
        method: "PUT",
        body: queryArg.orgIdPayload,
      }),
    }),
    putLeaveOrg: build.mutation<PutLeaveOrgApiResponse, PutLeaveOrgApiArg>({
      query: (queryArg) => ({
        url: `/api/account/organizations/leave`,
        method: "PUT",
        body: queryArg.orgIdPayload,
      }),
      invalidatesTags: ["Organizations"],
    }),
    putCancelRequest: build.mutation<
      PutCancelRequestApiResponse,
      PutCancelRequestApiArg
    >({
      query: (queryArg) => ({
        url: `/api/account/organizations/cancelMembershipRequest`,
        method: "PUT",
        body: queryArg.orgIdPayload,
      }),
      invalidatesTags: ["Organizations"],
    }),
    putResendMembershipRequest: build.mutation<
      PutResendMembershipRequestApiResponse,
      PutResendMembershipRequestApiArg
    >({
      query: (queryArg) => ({
        url: `/api/account/organizations/resendMembershipRequest`,
        method: "PUT",
        body: queryArg.orgIdPayload,
      }),
    }),
  }),
  overrideExisting: false,
});

export type GetAccountOrganizationsApiResponse =
  /** status 200 OK */ AccountOrgMembershipsQueryResult;
export type GetAccountOrganizationsApiArg = void;
export type GetAccountMembershipStructureApiResponse =
  /** status 200 OK */ AccountMembershipStructure;
export interface GetAccountMembershipStructureApiArg {
  orgId: string;
}
export type PostOrganizationsApiResponse = /** status 200 OK */ OrgIdResponse;
export interface PostOrganizationsApiArg {
  newOrganizationPayload: NewOrganizationPayload;
}
export type PutRequestOrgMembershipApiResponse =
  /** status 200 OK */ OrgIdPayload;
export interface PutRequestOrgMembershipApiArg {
  orgIdPayload: OrgIdPayload;
}
export type PutAcceptOrgInviteApiResponse = /** status 200 OK */ OrgIdPayload;
export interface PutAcceptOrgInviteApiArg {
  orgIdPayload: OrgIdPayload;
}
export type PutRejectOrgInviteApiResponse = /** status 200 OK */ OrgIdPayload;
export interface PutRejectOrgInviteApiArg {
  orgIdPayload: OrgIdPayload;
}
export type PutLeaveOrgApiResponse = /** status 200 OK */ OrgIdPayload;
export interface PutLeaveOrgApiArg {
  orgIdPayload: OrgIdPayload;
}
export type PutCancelRequestApiResponse = /** status 200 OK */ OrgIdPayload;
export interface PutCancelRequestApiArg {
  orgIdPayload: OrgIdPayload;
}
export type PutResendMembershipRequestApiResponse =
  /** status 200 OK */ OrgIdPayload;
export interface PutResendMembershipRequestApiArg {
  orgIdPayload: OrgIdPayload;
}

export interface OrgIdPayload {
  organizationId: string;
}
export interface OrgIdResponse {
  organizationId: string;
}
export interface OrgMembershipDetails {
  orgId: string;
  orgName: string;
  avatarUrl: string;
  orgPath: string[];
  isVerified: boolean;
}
export interface OrganizationMembership {
  membershipStatus: "INVITED" | "PENDING" | "JOINED" | "AVAILABLE";
  membershipType: "MEMBER" | "GUEST";
  membershipEmail: string;
  organization: OrgMembershipDetails;
}
export interface AccountOrgMembershipsQueryResult {
  organizationMemberships: OrganizationMembership[];
}
/** Org hierarchy node. Trimmed to the fields the leave-org dialog consumes. */
export interface OrgNodeTree {
  orgId: string;
  name: string;
  address?: string;
  isVerified: boolean;
  children: OrgNodeTree[];
}
export interface AccountMembershipStructure {
  /** The objective org tree, independent of any user's membership. */
  structure: OrgNodeTree;
  /** Org ids (within the tree) where the current account is a JOINED member. */
  joinedOrgs: string[];
}
export interface AddressPayload {
  streetAddressLine1: string;
  streetAddressLine2?: string;
  city: string;
  stateOrProvince: string;
  zipcodeOrPostalCode: string;
  countryCode?: string;
  country?: string;
}
export interface ContactInfoPayload {
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber: PhoneNumberPayload;
  jobTitle?: string;
  country: string;
}
export interface BillingProfilePayload {
  address: AddressPayload;
  contact: ContactInfoPayload;
  additionalDetails: Record<string, string>;
}
export interface RawDocumentUpload {
  name?: string;
  bytes?: string;
}
export interface IntegratorProgramApplicationDetails {
  numberOfControlEngineers:
    | "ONE_TO_TEN"
    | "TEN_TO_FIFTY"
    | "FIFTY_TO_HUNDRED"
    | "HUNDRED_PLUS";
}
export interface IntegratorProgramApplication {
  useBusinessAddress: boolean;
  setAsBusinessAddress: boolean;
  additionalDetails: IntegratorProgramApplicationDetails;
}
export interface NewOrganizationPayload {
  orgName: string;
  orgType: "ENDUSER" | "DISTRIBUTOR" | "INDUCTIVE_AUTOMATION" | "INTEGRATOR";
  orgSubType?:
    | "INDUSTRIAL_ORGANIZATION"
    | "INTEGRATOR"
    | "OEM"
    | "EDUCATIONAL"
    | "OTHER";
  website?: string;
  approvedDomains?: string[];
  businessAddress?: AddressPayload;
  parentNodeId?: string;
  isDiscoverable: boolean;
  inviteEmailAddresses?: string[];
  primaryIndustry?: string;
  additionalIndustries?: string[];
  phoneNumber?: NullSafePhoneNumberPayload;
  billingProfileType: "PAY_NOW" | "PAY_LATER";
  billingProfile?: BillingProfilePayload;
  requirePurchaseOrders?: boolean;
  taxDocuments?: RawDocumentUpload[];
  integratorProgramPreference?:
    | "NOT_INTERESTED"
    | "WOULD_LIKE_TO_JOIN"
    | "ALREADY_REGISTERED";
  integratorProgramApplication?: IntegratorProgramApplication;
}

export const {
  useGetAccountOrganizationsQuery,
  useGetAccountMembershipStructureQuery,
  usePostOrganizationsMutation,
  usePutRequestOrgMembershipMutation,
  usePutAcceptOrgInviteMutation,
  usePutRejectOrgInviteMutation,
  usePutLeaveOrgMutation,
  usePutCancelRequestMutation,
  usePutResendMembershipRequestMutation,
} = injectedApi;
